package org.example.project.planner.components

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TopAppBar
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.focus.onFocusChanged
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.example.project.client.model.EventResponse
import org.example.project.planner.PlannerViewMode
import org.example.project.ui.icons.AppIcons
import org.example.project.ui.theme.LocalPlayfulShapes
import org.example.project.ui.theme.PlayfulColors

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun PlannerHeader(
    viewMode: PlannerViewMode,
    currentEventName: String = "",
    currentEventId: String? = null,
    events: List<EventResponse> = emptyList(),
    isSaving: Boolean = false,
    isTimelineEditMode: Boolean = false,
    onToggleTimelineEditMode: () -> Unit = {},
    onBackToList: () -> Unit,
    onReturnToPlan: () -> Unit = onBackToList,
    onStartCreateEvent: () -> Unit,
    onSelectEvent: (String) -> Unit = {},
    onSaveEvent: () -> Unit = {},
    onNameChange: (String) -> Unit = {},
    onDeleteEvent: () -> Unit = {},
    eventStartTime: String = "",
    eventEndTime: String = "",
    onOpenStartTimePicker: () -> Unit = {},
    onOpenEndTimePicker: () -> Unit = {},
    modifier: Modifier = Modifier
) {
    val shapes = LocalPlayfulShapes.current
    var isEventDropdownOpen by remember { mutableStateOf(false) }
    var isEditingName by remember { mutableStateOf(false) }
    var showDeleteConfirmation by remember { mutableStateOf(false) }
    val focusRequester = remember { FocusRequester() }

    LaunchedEffect(isEditingName) {
        if (isEditingName) {
            focusRequester.requestFocus()
        }
    }

    TopAppBar(
        title = {
            if (viewMode is PlannerViewMode.EventList) {
                Text(
                    text = "Zapisane wydarzenia",
                    fontWeight = FontWeight.Bold,
                    fontSize = 18.sp,
                    color = PlayfulColors.TextPrimary
                )
            } else {
                Box {
                    Column(
                        modifier = Modifier.padding(vertical = 2.dp, horizontal = 2.dp),
                        verticalArrangement = Arrangement.Center
                    ) {
                        Row(
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            if (isEditingName) {
                                BasicTextField(
                                    value = currentEventName,
                                    onValueChange = onNameChange,
                                    singleLine = true,
                                    textStyle = TextStyle(
                                        fontWeight = FontWeight.Bold,
                                        fontSize = 16.sp,
                                        color = PlayfulColors.TextPrimary
                                    ),
                                    cursorBrush = SolidColor(PlayfulColors.Coral),
                                    keyboardOptions = KeyboardOptions(imeAction = ImeAction.Done),
                                    keyboardActions = KeyboardActions(onDone = { isEditingName = false }),
                                    modifier = Modifier
                                        .widthIn(min = 100.dp, max = 220.dp)
                                        .focusRequester(focusRequester)
                                        .onFocusChanged { if (!it.isFocused) isEditingName = false }
                                        .padding(vertical = 2.dp, horizontal = 2.dp)
                                )
                            } else {
                                Text(
                                    text = currentEventName.ifBlank { "Nowe wydarzenie" },
                                    fontWeight = FontWeight.Bold,
                                    fontSize = 16.sp,
                                    maxLines = 1,
                                    overflow = TextOverflow.Ellipsis,
                                    color = PlayfulColors.TextPrimary,
                                    modifier = Modifier
                                        .clickable(
                                            onClickLabel = "Edytuj nazwę wydarzenia",
                                            role = Role.Button
                                        ) { isEditingName = true }
                                        .padding(vertical = 2.dp, horizontal = 2.dp)
                                )
                            }
                            IconButton(
                                onClick = { isEventDropdownOpen = true },
                                modifier = Modifier.size(24.dp)
                            ) {
                                Icon(
                                    imageVector = AppIcons.ChevronDown,
                                    contentDescription = "Rozwiń listę zapisanych wydarzeń",
                                    tint = PlayfulColors.Coral,
                                    modifier = Modifier.size(18.dp)
                                )
                            }
                        }

                        if (isTimelineEditMode) {
                            Row(
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.spacedBy(4.dp),
                                modifier = Modifier.padding(top = 2.dp)
                            ) {
                                Surface(
                                    onClick = onOpenStartTimePicker,
                                    shape = shapes.badge,
                                    color = PlayfulColors.CoralLight,
                                    border = BorderStroke(1.dp, PlayfulColors.CoralBorder)
                                ) {
                                    Row(
                                        verticalAlignment = Alignment.CenterVertically,
                                        modifier = Modifier.padding(horizontal = 6.dp, vertical = 2.dp)
                                    ) {
                                        Icon(
                                            imageVector = AppIcons.Edit,
                                            contentDescription = "Zmień godzinę rozpoczęcia",
                                            tint = PlayfulColors.Coral,
                                            modifier = Modifier.size(11.dp)
                                        )
                                        Spacer(modifier = Modifier.width(3.dp))
                                        Text(
                                            text = "Od: ${eventStartTime.ifBlank { "10:00" }}",
                                            fontSize = 11.sp,
                                            fontWeight = FontWeight.Bold,
                                            color = PlayfulColors.Coral
                                        )
                                    }
                                }

                                Text(
                                    text = "–",
                                    fontSize = 11.sp,
                                    fontWeight = FontWeight.Bold,
                                    color = PlayfulColors.TextSecondary
                                )

                                Surface(
                                    onClick = onOpenEndTimePicker,
                                    shape = shapes.badge,
                                    color = PlayfulColors.CoralLight,
                                    border = BorderStroke(1.dp, PlayfulColors.CoralBorder)
                                ) {
                                    Row(
                                        verticalAlignment = Alignment.CenterVertically,
                                        modifier = Modifier.padding(horizontal = 6.dp, vertical = 2.dp)
                                    ) {
                                        Icon(
                                            imageVector = AppIcons.Edit,
                                            contentDescription = "Zmień godzinę zakończenia",
                                            tint = PlayfulColors.Coral,
                                            modifier = Modifier.size(11.dp)
                                        )
                                        Spacer(modifier = Modifier.width(3.dp))
                                        Text(
                                            text = "Do: ${eventEndTime.ifBlank { "16:00" }}",
                                            fontSize = 11.sp,
                                            fontWeight = FontWeight.Bold,
                                            color = PlayfulColors.Coral
                                        )
                                    }
                                }
                            }
                        } else if (eventStartTime.isNotBlank() && eventEndTime.isNotBlank()) {
                            Text(
                                text = "$eventStartTime – $eventEndTime",
                                fontSize = 11.sp,
                                color = PlayfulColors.TextSecondary,
                                modifier = Modifier.padding(start = 2.dp, top = 1.dp)
                            )
                        }
                    }

                    // Dropdown selector of saved events
                    DropdownMenu(
                        expanded = isEventDropdownOpen,
                        onDismissRequest = { isEventDropdownOpen = false },
                        modifier = Modifier.background(PlayfulColors.Surface)
                    ) {
                        Text(
                            text = "Zapisane wydarzenia:",
                            fontSize = 11.sp,
                            fontWeight = FontWeight.Bold,
                            color = PlayfulColors.TextSecondary,
                            modifier = Modifier.padding(horizontal = 14.dp, vertical = 6.dp)
                        )

                        if (events.isEmpty()) {
                            DropdownMenuItem(
                                text = {
                                    Text(
                                        text = "Brak zapisanych wydarzeń",
                                        fontSize = 13.sp,
                                        color = PlayfulColors.TextMuted
                                    )
                                },
                                onClick = {},
                                enabled = false
                            )
                        } else {
                            events.forEach { event ->
                                val isSelected = (currentEventId == event.id)
                                DropdownMenuItem(
                                    leadingIcon = if (isSelected) {
                                        {
                                            Icon(
                                                imageVector = AppIcons.Check,
                                                contentDescription = "Aktywne wydarzenie",
                                                tint = PlayfulColors.Coral,
                                                modifier = Modifier.size(16.dp)
                                            )
                                        }
                                    } else null,
                                    text = {
                                        Column {
                                            Text(
                                                text = event.name,
                                                fontSize = 13.sp,
                                                fontWeight = if (isSelected) FontWeight.Bold else FontWeight.SemiBold,
                                                color = if (isSelected) PlayfulColors.Coral else PlayfulColors.TextPrimary
                                            )
                                            Text(
                                                text = "${event.startTime}–${event.endTime}",
                                                fontSize = 11.sp,
                                                color = PlayfulColors.TextSecondary
                                            )
                                        }
                                    },
                                    onClick = {
                                        isEventDropdownOpen = false
                                        if (!isSelected) {
                                            onSelectEvent(event.id)
                                        }
                                    }
                                )
                            }
                        }

                        HorizontalDivider(color = PlayfulColors.BorderSubtle)

                        DropdownMenuItem(
                            leadingIcon = {
                                Icon(
                                    imageVector = AppIcons.Add,
                                    contentDescription = null,
                                    tint = PlayfulColors.Coral,
                                    modifier = Modifier.size(16.dp)
                                )
                            },
                            text = {
                                Text(
                                    text = "Dodaj nowy",
                                    fontSize = 13.sp,
                                    fontWeight = FontWeight.Bold,
                                    color = PlayfulColors.Coral
                                )
                            },
                            onClick = {
                                isEventDropdownOpen = false
                                onStartCreateEvent()
                            }
                        )

                        DropdownMenuItem(
                            leadingIcon = {
                                Icon(
                                    imageVector = AppIcons.Planner,
                                    contentDescription = null,
                                    tint = PlayfulColors.TextSecondary,
                                    modifier = Modifier.size(16.dp)
                                )
                            },
                            text = {
                                Text(
                                    text = "Widok listy wydarzeń",
                                    fontSize = 13.sp,
                                    color = PlayfulColors.TextPrimary
                                )
                            },
                            onClick = {
                                isEventDropdownOpen = false
                                onBackToList()
                            }
                        )

                        if (viewMode is PlannerViewMode.EventDetail && currentEventId != null) {
                            HorizontalDivider(color = PlayfulColors.BorderSubtle)
                            DropdownMenuItem(
                                leadingIcon = {
                                    Icon(
                                        imageVector = AppIcons.Delete,
                                        contentDescription = null,
                                        tint = PlayfulColors.Error,
                                        modifier = Modifier.size(16.dp)
                                    )
                                },
                                text = {
                                    Text(
                                        text = "Usuń wydarzenie",
                                        fontSize = 13.sp,
                                        fontWeight = FontWeight.SemiBold,
                                        color = PlayfulColors.Error
                                    )
                                },
                                onClick = {
                                    isEventDropdownOpen = false
                                    showDeleteConfirmation = true
                                }
                            )
                        }
                    }
                }
            }
        },
        navigationIcon = {
            if (viewMode is PlannerViewMode.EventList) {
                IconButton(onClick = onReturnToPlan) {
                    Icon(
                        imageVector = AppIcons.ArrowBack,
                        contentDescription = "Wróć do planu",
                        tint = PlayfulColors.TextPrimary
                    )
                }
            }
        },
        actions = {
            if (viewMode is PlannerViewMode.EventList) {
                Button(
                    onClick = onStartCreateEvent,
                    shape = shapes.buttonSmall,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = PlayfulColors.Coral,
                        contentColor = PlayfulColors.TextOnPrimary
                    ),
                    modifier = Modifier.padding(end = 8.dp)
                ) {
                    Icon(
                        imageVector = AppIcons.Add,
                        contentDescription = null,
                        tint = PlayfulColors.TextOnPrimary,
                        modifier = Modifier.size(16.dp)
                    )
                    Spacer(modifier = Modifier.width(4.dp))
                    Text(
                        text = "Dodaj nowy",
                        fontWeight = FontWeight.SemiBold,
                        fontSize = 13.sp
                    )
                }
            } else {
                if (isTimelineEditMode) {
                    Button(
                        onClick = onToggleTimelineEditMode,
                        shape = shapes.buttonSmall,
                        colors = ButtonDefaults.buttonColors(
                            containerColor = PlayfulColors.Coral,
                            contentColor = PlayfulColors.TextOnPrimary
                        ),
                        modifier = Modifier.padding(end = 8.dp)
                    ) {
                        Icon(
                            imageVector = AppIcons.Check,
                            contentDescription = "Zakończ edycję osi czasu",
                            tint = PlayfulColors.TextOnPrimary,
                            modifier = Modifier.size(16.dp)
                        )
                        Spacer(modifier = Modifier.width(4.dp))
                        Text(
                            text = "Gotowe",
                            fontWeight = FontWeight.Bold,
                            fontSize = 13.sp
                        )
                    }
                } else {
                    IconButton(
                        onClick = onToggleTimelineEditMode,
                        modifier = Modifier.padding(end = 4.dp)
                    ) {
                        Icon(
                            imageVector = AppIcons.Edit,
                            contentDescription = "Włącz tryb edycji osi czasu",
                            tint = PlayfulColors.Coral,
                            modifier = Modifier.size(20.dp)
                        )
                    }
                }
            }
        },
        colors = TopAppBarDefaults.topAppBarColors(
            containerColor = PlayfulColors.Surface,
            titleContentColor = PlayfulColors.TextPrimary
        ),
        modifier = modifier
    )

    if (showDeleteConfirmation) {
        AlertDialog(
            onDismissRequest = { showDeleteConfirmation = false },
            title = {
                Text(
                    text = "Usuń wydarzenie",
                    fontWeight = FontWeight.Bold,
                    fontSize = 17.sp,
                    color = PlayfulColors.TextPrimary
                )
            },
            text = {
                Text(
                    text = "Czy na pewno chcesz usunąć wydarzenie „${currentEventName.ifBlank { "Nowe wydarzenie" }}”? Tej operacji nie można cofnąć.",
                    fontSize = 14.sp,
                    color = PlayfulColors.TextSecondary
                )
            },
            confirmButton = {
                TextButton(
                    onClick = {
                        showDeleteConfirmation = false
                        onDeleteEvent()
                    }
                ) {
                    Text(
                        text = "Usuń",
                        color = PlayfulColors.Error,
                        fontWeight = FontWeight.Bold
                    )
                }
            },
            dismissButton = {
                TextButton(onClick = { showDeleteConfirmation = false }) {
                    Text(
                        text = "Anuluj",
                        color = PlayfulColors.TextSecondary
                    )
                }
            }
        )
    }
}
